import UseCaseTemplate, { type UseCaseContent } from '../../components/UseCaseTemplate';

const content: UseCaseContent = {
  slug: 'ev-charging-parking',
  title: 'EV Charging Parking in India — Find Spots With Real Chargers | Parksy',
  description:
    "Find EV charging parking across India. Parksy surfaces parking spots with EV charging on the map, so you don't have to choose between charging and parking safely.",
  canonicalPath: '/ev-charging-parking',
  ogImage: 'https://parksy.rent/og/ev-charging-parking.png',
  heroIntro:
    "India's EV adoption is accelerating, but dedicated EV charging parking is still rare. Parksy helps EV owners find parking spots that list EV charging as a confirmed amenity — so you stop circling blocks looking for both a plug and a guard.",
  painPoints: [
    'Public charging stations often have no dedicated parking bay, leading to ICE-ing (combustion cars blocking chargers).',
    'Apartment EV owners struggle to find overnight charging + parking in the same spot.',
    'Highway stops mix paid parking with token chargers that may be broken or slow.',
    'Office parking rarely distinguishes between EV bays and regular slots.',
  ],
  solutions: [
    { title: 'Filter by EV charging', body: 'Toggle "EV charging" on the map to surface only spots that confirm charger availability — no guessing, no "is this actually a charger?".' },
    { title: 'See what kind of spot', body: 'Covered, gated, CCTV — filter EV spots the way you filter any other listing. Overnight EV charging needs security, not just plug access.' },
    { title: 'Direct contact, no app lock-in', body: 'Once you drop a seeker pin, you get an email match with the spot owner. No subscription, no commission, no app required.' },
  ],
  citySpotlights: [
    { slug: 'bangalore', name: 'Bangalore', note: 'EV adoption leader' },
    { slug: 'delhi', name: 'Delhi', note: 'FAME-II subsidy hub' },
    { slug: 'mumbai', name: 'Mumbai', note: 'High-rise EV retrofit demand' },
    { slug: 'hyderabad', name: 'Hyderabad', note: 'IT corridor EVs' },
    { slug: 'pune', name: 'Pune', note: 'Two-wheeler EV hotspot' },
    { slug: 'chennai', name: 'Chennai', note: 'Auto industry EV base' },
  ],
  faqs: [
    { question: 'Are EV charging spots on Parksy always free to park?', answer: "Not necessarily. The parking cost depends on the spot owner — Parksy shows whatever rate the owner lists. Some may include charging in the parking fee; others may treat charging as a separate cost agreed with the owner." },
    { question: 'What EV connector types are supported?', answer: "Parksy doesn't enforce a connector standard — we just confirm that the spot has an EV charging point. The connector type (Bharat AC-001, Type 2, CCS2) is up to the spot owner to describe in the listing." },
    { question: 'Can I list my private charger as a public EV parking spot?', answer: "Yes, if you have a private charger in a driveway, garage, or commercial lot and want to share access, you can list it. The owner sets the rate, the matching engine connects you with EV owners within 3 km." },
    { question: 'How fast is EV charging at typical Parksy spots?', answer: "Most residential listings will be slow AC chargers (3–7 kW), good for overnight. Commercial lots may have DC fast chargers. The owner specifies the speed in the listing." },
    { question: 'Does Parksy verify the EV charging claim?', answer: "We rely on community reports and outlier detection. If a spot is listed with EV charging but the charger is broken or fake, three reports auto-hide it. We encourage EV owners to leave ratings." },
  ],
  primaryCta: { label: 'Find EV parking on the map →', to: '/', color: 'bg-parksy-600 hover:bg-parksy-700' },
  secondaryCta: { label: 'List your charger', to: '/list-your-parking' },
  breadcrumbs: [
    { label: 'Home', to: '/' },
    { label: 'EV charging parking' },
  ],
};

export default function EVChargingParking() {
  return <UseCaseTemplate content={content} />;
}

